import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["search", "slots", "slot", "placeholder", "chip", "provisional", "picker", "options", "error", "submit", "form", "footer", "results", "header"]
  static values = { initialUrl: String, native: Boolean }

  connect() {
    this.selection = []
    this.editingIndex = null
    this.renderSelection()

    if (this.initialUrlValue) {
      const url = this.initialUrlValue
      this.initialUrlValue = ""
      this.loadOptions(url, this.add({ id: null, chip: null, provisional: true }))
    }
  }

  disconnect() {
    this.selection.forEach(entry => entry.request?.abort())
    this.editRequest?.abort()
    this.resizeAnimation?.cancel()
  }

  // Tapping a result row adds that exact product right away, or removes it if
  // it is already in a slot. The options request runs behind the provisional
  // tile: it either confirms the pick or opens a variant / formula picker.
  choose(event) {
    const button = event.currentTarget
    const index = this.productIds.indexOf(button.dataset.productId)

    if (index >= 0) {
      this.removeAt(index)
      this.focusSearch()
      return
    }

    if (this.selection.length >= this.maxSelectable) return

    this.errorTarget.classList.add("hidden")
    const entry = this.add({ id: button.dataset.productId, chip: this.provisionalChip(button), provisional: true })
    this.originButton = button
    this.loadOptions(button.dataset.optionsUrl, entry)
  }

  edit(event) {
    if (this.editRequest) return
    const button = event.currentTarget
    const entry = this.selection[this.slotIndexFor(button)]
    if (!entry || entry.request) return

    this.originButton = button
    this.loadOptions(button.dataset.optionsUrl, entry, { editing: true })
  }

  remove(event) {
    this.removeAt(this.slotIndexFor(event.currentTarget))
    this.focusSearch()
  }

  chooseOption(event) {
    const entry = this.selection[this.editingIndex]
    if (!entry) return
    this.loadOptions(event.currentTarget.dataset.optionsUrl, entry, { editing: true })
  }

  add(entry) {
    this.selection.push(entry)
    this.renderSelection()
    return entry
  }

  removeAt(index) {
    const entry = this.selection[index]
    if (!entry) return

    entry.request?.abort()
    if (this.editingIndex === index) this.closePicker()
    else if (Number.isInteger(this.editingIndex) && this.editingIndex > index) this.editingIndex -= 1
    this.selection.splice(index, 1)
    this.renderSelection()
  }

  async loadOptions(url, entry, { editing = false } = {}) {
    const request = new AbortController()
    if (editing) {
      this.editRequest?.abort()
      this.editRequest = request
      this.editingIndex = this.selection.indexOf(entry)
      this.pickerTarget.setAttribute("aria-busy", "true")
    } else {
      entry.request = request
    }

    try {
      const response = await fetch(url, { headers: { Accept: "text/html" }, signal: request.signal })
      if (!response.ok) throw new Error(`Product options: ${response.status}`)

      const content = document.createElement("template")
      content.innerHTML = await response.text()
      const chip = content.content.querySelector(".compare-search-product-select")

      if (chip) {
        this.confirm(entry, chip)
      } else {
        this.editingIndex = this.selection.indexOf(entry)
        this.showPicker(content.content)
      }
    } catch (error) {
      if (error.name === "AbortError") return
      this.showError("We couldn’t load this product’s options. Please try again.")
      if (!editing) this.removeAt(this.selection.indexOf(entry))
    } finally {
      if (entry.request === request) entry.request = null
      if (this.editRequest === request) {
        this.editRequest = null
        this.pickerTarget.removeAttribute("aria-busy")
      }
      this.optionsTarget.querySelectorAll('[data-controller~="loading-button"]').forEach(button => {
        button.dispatchEvent(new CustomEvent("loading-button:stop"))
      })
      this.renderSelection()
    }
  }

  // A chosen variant or formula may be a different product than the row that
  // was tapped, so the entry takes on the id the server confirmed.
  confirm(entry, chip) {
    const id = chip.dataset.productId
    const index = this.selection.indexOf(entry)
    if (index < 0) return

    if (this.selection.some((other, otherIndex) => other.id === id && otherIndex !== index)) {
      this.showError("That product is already selected. Choose a different variant or formula.")
      return
    }

    this.selection[index] = { id, chip }
    if (this.editingIndex === index) {
      this.editingIndex = null
      this.showSearch()
      this.searchTarget.querySelector("input")?.focus({ preventScroll: true })
    }
  }

  provisionalChip(button) {
    const chip = this.provisionalTarget.content.firstElementChild.cloneNode(true)
    const image = button.querySelector("img")
    chip.dataset.productId = button.dataset.productId
    chip.querySelector("img").src = image?.currentSrc || image?.src || ""
    chip.querySelector("[data-action]").setAttribute("aria-label", `Remove ${button.dataset.productName}`)
    return chip
  }

  showPicker(content) {
    this.resizeDrawer(() => {
      this.optionsTarget.replaceChildren(content)
      if (this.pickerTarget.classList.contains("hidden")) {
        this.searchScrollTop = this.resultsTarget.scrollTop
      }
      this.searchTarget.classList.add("hidden")
      this.resultsTarget.classList.add("hidden")
      this.footerTarget.classList.add("hidden")
      this.headerTarget.classList.add("hidden")
      this.element.dataset.comparisonPicking = "true"
      this.pickerTarget.classList.remove("hidden")
      this.pickerTarget.scrollTop = 0
      this.optionsTarget.querySelector("[data-comparison-products-heading]")?.focus({ preventScroll: true })
    })
  }

  showSearch() {
    this.resizeDrawer(() => {
      const wasPicking = !this.pickerTarget.classList.contains("hidden")
      this.pickerTarget.classList.add("hidden")
      this.optionsTarget.replaceChildren()
      this.searchTarget.classList.remove("hidden")
      this.resultsTarget.classList.remove("hidden")
      this.footerTarget.classList.remove("hidden")
      this.headerTarget.classList.remove("hidden")
      delete this.element.dataset.comparisonPicking
      if (wasPicking) this.resultsTarget.scrollTop = this.searchScrollTop || 0
    })
  }

  resizeDrawer(update) {
    const drawer = this.element.closest(".drawer-content")
    const animate = drawer?.closest(".bottom-drawer.show") &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    const previousHeight = animate ? drawer.getBoundingClientRect().height : 0
    this.resizeAnimation?.cancel()

    update()

    if (!animate) return
    const nextHeight = drawer.getBoundingClientRect().height
    if (Math.abs(nextHeight - previousHeight) < 1) return

    // Animate measured bounds so both auto-sized and fixed-height steps interpolate.
    // The original responsive sizing takes over again when the animation finishes.
    this.resizeAnimation = drawer.animate([
      { minHeight: `${previousHeight}px`, maxHeight: `${previousHeight}px` },
      { minHeight: `${nextHeight}px`, maxHeight: `${nextHeight}px` }
    ], { duration: 280, easing: "cubic-bezier(0.22, 1, 0.36, 1)" })
  }

  // Backing out of a picker keeps a confirmed product as it was, but drops a
  // provisional one: the tap that opened the picker never settled on a product.
  cancel() {
    const index = this.editingIndex
    const entry = this.selection[index]
    const origin = this.originButton

    this.closePicker()
    if (entry?.provisional) this.selection.splice(index, 1)
    this.renderSelection()

    if (origin?.isConnected) {
      origin.focus({ preventScroll: true })
    } else {
      this.searchTarget.querySelector("input")?.focus({ preventScroll: true })
    }
  }

  closePicker() {
    this.editRequest?.abort()
    this.editRequest = null
    this.editingIndex = null
    this.pickerTarget.removeAttribute("aria-busy")
    this.errorTarget.classList.add("hidden")
    this.showSearch()
  }

  resetPicker() {
    this.resultsTarget.scrollTop = 0
  }

  renderSelection() {
    const count = this.selection.length
    const max = this.maxSelectable

    this.slotTargets.forEach((slot, index) => {
      const entry = this.selection[index]
      const placeholder = slot.querySelector('[data-comparison-products-target="placeholder"]')
      const chipContainer = slot.querySelector('[data-comparison-products-target="chip"]')

      if (entry?.chip) {
        if (chipContainer.firstElementChild !== entry.chip) chipContainer.replaceChildren(entry.chip)
        chipContainer.classList.remove("hidden")
        placeholder.classList.add("hidden")
      } else {
        chipContainer.replaceChildren()
        chipContainer.classList.add("hidden")
        placeholder.classList.remove("hidden")
      }
    })

    this.element.querySelectorAll('[data-action~="comparison-products#choose"]').forEach(button => {
      const selected = this.productIds.includes(button.dataset.productId)
      button.disabled = !selected && count >= max
      button.setAttribute("aria-label", `${selected ? "Remove" : "Add"} ${button.dataset.productName}`)
      this.renderIndicator(button.querySelector("[data-selection-indicator]"), selected)
    })

    const ready = count >= 2 && this.selection.every(entry => entry.id)
    this.submitTarget.disabled = this.submitting || !ready
    this.submitTarget.querySelector('[data-loading-button-target="text"]').textContent =
      count > 1 ? `Compare ${count} products` : "Add at least two products"

    this.formTarget.querySelectorAll("select").forEach((select, index) => {
      const id = this.productIds[index]
      select.replaceChildren(...(id ? [new Option(id, id, true, true)] : []))
      select.dispatchEvent(new Event("change", { bubbles: true }))
    })
  }

  renderIndicator(indicator, selected) {
    if (!indicator) return
    indicator.classList.toggle("bg-indigo-900", selected)
    indicator.classList.toggle("border-indigo-900", selected)
    indicator.classList.toggle("border-warm-gray-300", !selected)
    indicator.querySelector("[data-selection-indicator-add]").classList.toggle("hidden", selected)
    indicator.querySelector("[data-selection-indicator-added]").classList.toggle("hidden", !selected)
  }

  submit() {
    if (this.submitTarget.disabled) return
    this.submitting = true
    this.submitTarget.disabled = true
    this.application.getControllerForElementAndIdentifier(this.submitTarget, "loading-button")?.showLoading()
    this.formTarget.requestSubmit()
  }

  submitted() {
    this.submitting = false
    this.renderSelection()
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  focusSearch() {
    if (this.nativeValue && this.selection.length > 0) return
    this.searchTarget.querySelector("input")?.focus({ preventScroll: true })
  }

  slotIndexFor(element) {
    return Number(element.closest("[data-slot-index]").dataset.slotIndex)
  }

  get maxSelectable() {
    return Number(this.slotsTarget.dataset.maxSelectable)
  }

  get productIds() {
    return this.selection.map(entry => entry.id)
  }
}
